import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Field, Modal, TextArea } from "../components/ui";
import AppHeader from "../components/AppHeader";
import { Clipboard, Scissors, Wrench, ChevronDown, CheckCircle2 } from "lucide-react";
import { SamplingItemsEditor, newSamplingItem } from "../components/SamplingItemsEditor";
import { ProductionItemsEditor, newProductionStyle } from "../components/ProductionItemsEditor";

const TYPES = [
  { key: "initial_meeting", label: "Initial Cost Estimates", hint: "Cost estimates & terms", Icon: Clipboard },
  { key: "sampling", label: "Sampling Quote", hint: "Style, fabric, charges", Icon: Scissors },
  { key: "production", label: "Production Quote", hint: "Per-piece costing", Icon: Wrench },
];

export default function QuoteNew() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const forceType = sp.get("type"); // "initial_meeting" | "sampling" | "production" | null
  const initialType = forceType === "initial_meeting" || forceType === "production" || forceType === "sampling" ? forceType : "sampling";
  const [type, setType] = useState(initialType);
  // If arriving with ?type=initial_meeting show only that tile; otherwise show
  // the commercial-quote tiles (sampling + production). Keeps the two flows
  // visually distinct without hiding the picker entirely.
  const visibleTypes = forceType === "initial_meeting"
    ? TYPES.filter((t) => t.key === "initial_meeting")
    : TYPES.filter((t) => t.key !== "initial_meeting");
  const [customers, setCustomers] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [customerQuery, setCustomerQuery] = useState("");
  const [customer, setCustomer] = useState(
    sp.get("customer_id") ? { id: sp.get("customer_id"), name: sp.get("customer_name") || "" } : null
  );
  const [data, setData] = useState({});
  const [samplingItems, setSamplingItems] = useState([newSamplingItem()]);
  const [prodItems, setProdItems] = useState([newProductionStyle()]);
  const [prodMaterials, setProdMaterials] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { api.get("/customers").then(setCustomers).catch(() => {}); }, []);
  const setD = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const submit = async () => {
    setError("");
    if (!customer) { setError("Please select a customer first"); return; }
    try {
      setBusy(true);
      const payload =
        type === "sampling" ? {
          ...data, gst_rate: data.gst_rate || "5",
          items: samplingItems.map((it) => ({
            image: it.image || null, product_name: it.product_name || "",
            num_samples: it.num_samples || "1", pattern_base: it.pattern_base || "0",
            pattern_grading: it.pattern_grading || "0", grading_sizes: it.grading_sizes || "0",
            sampling_charges: it.sampling_charges || "0",
          })),
        } : type === "production" ? {
          ...data, gst_rate: data.gst_rate || "5",
          items: prodItems.map((it) => ({
            image: it.image || null, product_name: it.product_name || "", style_no: it.style_no || "",
            num_pieces: it.num_pieces || "1", pattern_base: it.pattern_base || "0",
            pattern_grading: it.pattern_grading || "0", grading_sizes: it.grading_sizes || "0",
            pricing_type: it.pricing_type || "fob", production_price: it.production_price || "0",
          })),
          materials: prodMaterials.map((m) => ({ name: m.name || "", quantity: m.quantity || "0", price: m.price || "0" })),
        } : data;

      const q = await api.post("/quotes", {
        customer_id: customer.id, customer_name: customer.name, brand_name: customer.brand_name,
        quote_type: type, title: data.title || "", data: payload,
      });
      navigate(`/quotes/${q.quote_id}`);
    } catch (e) { setError(e?.message || "Could not create"); } finally { setBusy(false); }
  };

  return (
    <>
      <AppHeader title={forceType === "initial_meeting" ? "New Initial Doc" : "New Quote"} back />
      <div className="sg-main sg-fade-in">
        <label className="sg-label" style={{ marginTop: 0 }}>Document Type</label>
        {visibleTypes.map((t) => {
          const active = type === t.key;
          return (
            <button
              key={t.key} onClick={() => setType(t.key)}
              data-testid={`quote-type-${t.key}`}
              style={{
                display: "flex", alignItems: "center", gap: 12, width: "100%",
                padding: 14, marginBottom: 8, borderRadius: 12, cursor: "pointer",
                border: `1px solid ${active ? "var(--brand)" : "var(--line)"}`,
                background: active ? "var(--brand)" : "var(--surface)",
                color: active ? "#fff" : "var(--ink)", textAlign: "left",
              }}>
              <t.Icon size={18} color={active ? "#fff" : "var(--brand)"} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700 }}>{t.label}</div>
                <div style={{ fontSize: 11, color: active ? "rgba(255,255,255,0.78)" : "var(--muted)", marginTop: 2 }}>{t.hint}</div>
              </div>
              {active && <CheckCircle2 size={18} />}
            </button>
          );
        })}

        <label className="sg-label" style={{ marginTop: 14 }}>Customer</label>
        <button onClick={() => setPickerOpen(true)} className="sg-input" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", textAlign: "left", cursor: "pointer", marginBottom: 14 }} data-testid="quote-customer-picker">
          <span style={{ color: customer ? "var(--ink)" : "var(--muted)" }}>{customer ? customer.name : "Select customer"}</span>
          <ChevronDown size={16} color="var(--muted)" />
        </button>

        {type === "initial_meeting" && (
          <>
            <Field label="Studio Grape representative" value={data.studio_rep || ""} onChange={(e) => setD("studio_rep", e.target.value)} placeholder="Your name" />
            <Field label="Category" value={data.category || ""} onChange={(e) => setD("category", e.target.value)} placeholder="Womenswear, kidswear..." />
            <Field label="Sampling price from (₹)" inputMode="numeric" value={data.sampling_price_from || ""} onChange={(e) => setD("sampling_price_from", e.target.value)} placeholder="2000" />
            <Field label="Sampling price to (optional)" inputMode="numeric" value={data.sampling_price_to || ""} onChange={(e) => setD("sampling_price_to", e.target.value)} placeholder="Leave empty for 'onwards'" />
            <Field label="Base pattern making charges (₹)" inputMode="numeric" value={data.pattern_charges || ""} onChange={(e) => setD("pattern_charges", e.target.value)} />
            <Field label="Pattern grading charges (₹)" inputMode="numeric" value={data.grading_charges || ""} onChange={(e) => setD("grading_charges", e.target.value)} />
            <Field label="Tentative sampling commencement" value={data.sampling_start || ""} onChange={(e) => setD("sampling_start", e.target.value)} placeholder="e.g. 10 Mar 2026" />
            <TextArea label="MOQs discussed" value={data.moq_details || ""} onChange={(e) => setD("moq_details", e.target.value)} placeholder="e.g. 300 pcs per design" />
            <Field label="Production price (₹ / pc)" inputMode="numeric" value={data.production_price || ""} onChange={(e) => setD("production_price", e.target.value)} />
          </>
        )}

        {type === "sampling" && (
          <>
            <Field label="Title (optional)" value={data.title || ""} onChange={(e) => setD("title", e.target.value)} placeholder="e.g. SS-26 Linen Collection" />
            <Field label="GST %" inputMode="numeric" value={data.gst_rate || "5"} onChange={(e) => setD("gst_rate", e.target.value)} />
            <SamplingItemsEditor items={samplingItems} onChange={setSamplingItems} gstRate={Number(data.gst_rate || 5)} />
            <TextArea label="Special instructions" value={data.special_instructions || ""} onChange={(e) => setD("special_instructions", e.target.value)} />
          </>
        )}

        {type === "production" && (
          <>
            <Field label="Title (optional)" value={data.title || ""} onChange={(e) => setD("title", e.target.value)} placeholder="e.g. SS-26 Production Run" />
            <Field label="GST %" inputMode="numeric" value={data.gst_rate || "5"} onChange={(e) => setD("gst_rate", e.target.value)} />
            <ProductionItemsEditor items={prodItems} materials={prodMaterials} onItemsChange={setProdItems} onMaterialsChange={setProdMaterials} gstRate={Number(data.gst_rate || 5)} />
          </>
        )}

        {error && <div style={{ color: "var(--danger)", fontSize: 13, marginBottom: 12 }}>{error}</div>}

        <button className="sg-btn sg-btn-primary" onClick={submit} disabled={busy} data-testid="quote-create" style={{ width: "100%", marginTop: 12 }}>
          {busy ? "Creating..." : "Create Quote"}
        </button>
      </div>

      <Modal open={pickerOpen} onClose={() => { setPickerOpen(false); setCustomerQuery(""); }} title="Pick customer">
        <div style={{ position: "sticky", top: 0, background: "var(--surface)", paddingBottom: 8, zIndex: 1 }}>
          <input
            className="sg-input"
            autoFocus
            placeholder="Search company or brand"
            value={customerQuery}
            onChange={(e) => setCustomerQuery(e.target.value)}
            data-testid="quote-customer-search"
          />
        </div>
        {(() => {
          const q = customerQuery.trim().toLowerCase();
          const filtered = q
            ? customers.filter((c) =>
                (c.company_name || "").toLowerCase().includes(q) ||
                (c.brand_name || "").toLowerCase().includes(q) ||
                (c.contact_person || "").toLowerCase().includes(q)
              )
            : customers;
          if (customers.length === 0) return <div style={{ padding: 20, textAlign: "center", color: "var(--muted)" }}>No customers yet. Create one first.</div>;
          if (filtered.length === 0) return <div style={{ padding: 20, textAlign: "center", color: "var(--muted)" }}>No customers match &ldquo;{customerQuery}&rdquo;.</div>;
          return filtered.map((c) => (
            <button key={c.customer_id}
              onClick={() => { setCustomer({ id: c.customer_id, name: c.company_name, brand_name: c.brand_name }); setPickerOpen(false); setCustomerQuery(""); }}
              data-testid={`pick-${c.customer_id}`}
              style={{ display: "block", padding: "14px 6px", background: "transparent", border: 0, borderBottom: "1px solid var(--line)", width: "100%", textAlign: "left", cursor: "pointer" }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>{c.company_name}</div>
              <div style={{ fontSize: 12, color: "var(--muted)" }}>{c.brand_name}</div>
            </button>
          ));
        })()}
      </Modal>
    </>
  );
}
